import type { CompaniesSearch, SortDirection, SortKey } from './companies-search.schema';

/**
 * Sort dropdown options as presented to the user. Each pairs a (sort, dir)
 * with a label. The "no selection" state is represented by the absence of
 * `search.sort` in the URL — not a separate option here.
 */
export interface SortOption {
  /** Discriminator stored at `search.sort` */
  sort: SortKey;
  /** Discriminator stored at `search.dir` */
  dir: SortDirection;
  /** UI label, pt-BR */
  label: string;
}

/**
 * The 4 dropdown choices specified by the Figma + spec 025 §FR-011.
 * Each option is a single direction (matches Figma simplification — no
 * separate asc/desc per option). Ordering matches the design.
 */
export const SORT_OPTIONS: readonly SortOption[] = [
  { sort: 'createdAt', dir: 'desc', label: 'Data de criação' },
  { sort: 'name', dir: 'asc', label: 'Ordem alfabética' },
  { sort: 'status', dir: 'desc', label: 'Status' },
  { sort: 'updatedAt', dir: 'desc', label: 'Última modificação' },
] as const;

/**
 * Default sort applied when the user clicks the table-view toggle WITHOUT
 * a prior sort selection. Matches Figma: status DESC = ativos primeiro.
 */
export const DEFAULT_TABLE_SORT: { sort: SortKey; dir: SortDirection } = {
  sort: 'status',
  dir: 'desc',
};

/**
 * Derived UI state from the URL search params. Computed via pure mapping —
 * the search params are the source of truth.
 */
export interface DerivedViewState {
  view: CompaniesSearch['view'];
  /** Whether the sort `arrowdownup` button shows the "selected" visual state. */
  isSortSelected: boolean;
  /** Whether the `table` button shows the "selected" visual state. */
  isTableSelected: boolean;
  /** Currently selected sort option, if any (matched by sort+dir). */
  selectedSortOption: SortOption | undefined;
}

export function deriveViewState(search: CompaniesSearch): DerivedViewState {
  // The table view auto-applies `status DESC` when toggled without a prior
  // sort. Treat that auto-default as "no explicit selection" so the sort
  // button doesn't render as selected just because the user clicked the
  // table toggle. Any other sort/dir → explicit user choice → button selected.
  const isTableAutoDefault =
    search.view === 'table' &&
    search.sort === DEFAULT_TABLE_SORT.sort &&
    search.dir === DEFAULT_TABLE_SORT.dir;
  return {
    view: search.view,
    isSortSelected: search.sort !== undefined && !isTableAutoDefault,
    isTableSelected: search.view === 'table',
    selectedSortOption:
      search.sort !== undefined && !isTableAutoDefault
        ? SORT_OPTIONS.find((o) => o.sort === search.sort && o.dir === search.dir)
        : undefined,
  };
}

/**
 * Apply a sort selection from the dropdown. Mutates `sort` + `dir` and resets
 * `page` to 1 (changing sort means reordering the result set; page numbers
 * become meaningless across the new ordering).
 */
export function applySortChange(
  search: CompaniesSearch,
  option: { sort: SortKey; dir: SortDirection }
): CompaniesSearch {
  return {
    ...search,
    sort: option.sort,
    dir: option.dir,
    page: 1,
  };
}

/**
 * Clear any active sort. Returns to the server's default ordering
 * (`createdAt DESC`). Used by the dropdown's "remover ordenação" entry.
 */
export function clearSortChange(search: CompaniesSearch): CompaniesSearch {
  return {
    ...search,
    sort: undefined,
    dir: 'asc',
    page: 1,
  };
}

/**
 * Toggle between cards and table views, preserving any active sort. When
 * switching cards → table without a sort, applies the table-view default
 * (`status DESC`) per spec 025 §FR-012. Switching table → cards keeps
 * the sort intact (FR-014).
 */
export function applyToggleView(search: CompaniesSearch): CompaniesSearch {
  if (search.view === 'table') {
    // table → cards. If the current sort is the table's auto-default
    // (status DESC), drop it — the user never picked it, so cards should
    // return to its own "no sort" baseline. Any other sort was an explicit
    // user pick and is preserved per spec 025 §FR-014.
    const isTableAutoDefault =
      search.sort === DEFAULT_TABLE_SORT.sort && search.dir === DEFAULT_TABLE_SORT.dir;
    if (isTableAutoDefault) {
      return { ...search, view: 'cards', sort: undefined, dir: 'asc', page: 1 };
    }
    return { ...search, view: 'cards', page: 1 };
  }
  // cards → table: if no sort yet, default to status DESC. Otherwise keep current sort.
  if (search.sort === undefined) {
    return {
      ...search,
      view: 'table',
      sort: DEFAULT_TABLE_SORT.sort,
      dir: DEFAULT_TABLE_SORT.dir,
      page: 1,
    };
  }
  return { ...search, view: 'table', page: 1 };
}
